/**
 * OdooModulesPage (P3-B Doc 37) — list installed Odoo modules + activate.
 *
 * Curated modules first (sale/purchase/hr_holidays/...), then non-curated.
 * Activate sync (5-30s) → refresh list → toast.
 */
import { Search } from "lucide-react";
import * as React from "react";

import { type OdooModule, odooModulesApi } from "@/api/odooModules";
import { ModuleCard } from "@/components/admin/ModuleCard";
import { EmptyData } from "@/components/illustrations/EmptyData";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { LoadingCards } from "@/components/ui/loading-cards";
import { useRouteStore } from "@/stores/route";
import { useUIStore } from "@/stores/ui";

export function OdooModulesPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const navigate = useRouteStore((s) => s.navigate);

  const [items, setItems] = React.useState<OdooModule[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("");
  const [busyMap, setBusyMap] = React.useState<Set<string>>(new Set());
  const [showAll, setShowAll] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await odooModulesApi.list();
      setItems(res.items);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không tải được modules",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [pushToast]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleActivate = async (mod: OdooModule): Promise<void> => {
    if (
      mod.status === "done" &&
      !confirm(
        `Module ${mod.name} đã activate. Re-sync sẽ update các form drafts. Tiếp tục?`,
      )
    ) {
      return;
    }
    setBusyMap((s) => new Set(s).add(mod.name));
    try {
      const res = await odooModulesApi.activate(mod.name);
      if (res.status === "done") {
        pushToast({
          variant: "success",
          title: `Đã gen ${res.generated_form_codes.length} form draft`,
          description: `Module: ${mod.name}${
            res.skipped_models.length > 0
              ? ` · Skipped: ${res.skipped_models.length}`
              : ""
          }`,
        });
      } else {
        pushToast({
          variant: "destructive",
          title: "Activate lỗi",
          description: res.error ?? "Không gen được form nào",
        });
      }
      await refresh();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Activate thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusyMap((s) => {
        const next = new Set(s);
        next.delete(mod.name);
        return next;
      });
    }
  };

  const filtered = React.useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = items;
    if (!showAll) list = list.filter((it) => it.is_curated);
    if (q.length > 0) {
      list = list.filter(
        (it) =>
          it.name.toLowerCase().includes(q) ||
          it.odoo_display_name.toLowerCase().includes(q) ||
          (it.label_vi ?? "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [items, filter, showAll]);

  const curatedCount = items.filter((it) => it.is_curated).length;
  const nonCuratedCount = items.length - curatedCount;

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      {/* Header */}
      <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-semibold">Odoo Modules</h1>
          <p className="text-sm text-muted-foreground">
            Activate module → AI auto-gen forms cho các model chính. Drafts
            cần admin review + publish.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Tìm module..."
              className="pl-8 h-8 text-sm w-[200px]"
            />
          </div>
        </div>
      </header>

      {/* Body */}
      {loading ? (
        <LoadingCards count={6} />
      ) : items.length === 0 ? (
        <EmptyState
          hero={<EmptyData size={120} />}
          title="Chưa có module nào"
          description="Odoo của bạn chưa có module nào được install. Cài đặt module trong Odoo trước."
        />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map((mod) => (
              <ModuleCard
                key={mod.name}
                module={mod}
                busy={busyMap.has(mod.name)}
                onActivate={() => void handleActivate(mod)}
                onViewForms={() => navigate("admin.forms")}
              />
            ))}
          </div>

          {/* Show all toggle */}
          {nonCuratedCount > 0 ? (
            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                {showAll
                  ? `Chỉ hiển thị ${curatedCount} module recommended`
                  : `Hiển thị tất cả ${items.length} modules (${nonCuratedCount} custom)`}
              </button>
            </div>
          ) : null}

          {filtered.length === 0 && filter ? (
            <div className="text-center text-sm text-muted-foreground py-8">
              Không có module nào match "{filter}"
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
